import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  clientsApi,
  type ClientSummaryDto,
} from "@/lib/clients-accounts";
import { useAuth } from "@/app/auth-context";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { NewClientDialog } from "@/components/new-client-dialog";

// ---------------------------------------------------------------------------
// Filter state — single search + chip-based status grouping
// ---------------------------------------------------------------------------

type StatusChipId = "active" | "archived" | "all";

const CHIPS_ADMIN = [
  { id: "active" as const,   label: "Show Active" },
  { id: "archived" as const, label: "Archived" },
  { id: "all" as const,      label: "All" },
];
// Non-admins always see active-only; chip bar hidden for them.

// ---------------------------------------------------------------------------
// Build query string from filter state
// ---------------------------------------------------------------------------

function buildQueryString(page: number, q: string, chip: StatusChipId): string {
  const parts = [`page=${page}&pageSize=50`];
  if (q) parts.push(`q=${encodeURIComponent(q)}`);
  // Map chips to `deleted` API param: active=false, archived=true, all=omit.
  if (chip === "active") parts.push("deleted=false");
  else if (chip === "archived") parts.push("deleted=true");
  return parts.join("&");
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function ClientsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<StatusChipId>("active");
  const [creating, setCreating] = useState(false);

  const { me } = useAuth();
  const isAdmin = me?.roles.includes("Admin") ?? false;

  // Non-admins are forced to "active" — clamp the chip server-side too.
  const effectiveChip: StatusChipId = isAdmin ? chip : "active";

  const qs = buildQueryString(page, q, effectiveChip);

  const { data, isLoading } = useQuery({
    queryKey: ["clients", page, q, effectiveChip],
    queryFn: () => clientsApi.list(qs),
  });

  const cols: DataGridColumn<ClientSummaryDto>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (r) => (
        <Link to={`/clients/${r.clientId}`} className="underline text-primary">
          {r.fullName}
        </Link>
      ),
    },
    { id: "company", header: "Company", sortable: true, accessor: (r) => r.companyName ?? "—" },
    { id: "email",   header: "Email",   sortable: true, accessor: (r) => r.emailAddress ?? "—" },
    { id: "phone",   header: "Phone",   sortable: true, accessor: (r) => r.phone ?? "—" },
    { id: "branch",  header: "Branch",  sortable: true, accessor: (r) => r.branch ?? "—" },
    {
      id: "credit",
      header: "Has credit",
      sortable: true,
      accessor: (r) => (r.hasApprovedCreditAccount ? "✓" : "—"),
    },
  ];

  // Sort state — client-side only; clientsApi.list has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: ClientSummaryDto): string | number | null => {
      switch (sort.columnId) {
        case "name":    return r.fullName;
        case "company": return r.companyName ?? null;
        case "email":   return r.emailAddress ?? null;
        case "phone":   return r.phone ?? null;
        case "branch":  return r.branch ?? null;
        case "credit":  return r.hasApprovedCreditAccount ? 1 : 0;
        default:        return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  return (
    <ListPage
      title="Clients"
      primaryAction={
        <Button onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" /> Add Client</Button>
      }
      search={{
        value: q,
        onChange: (v) => { setQ(v); setPage(0); },
        placeholder: "Search name, email, company…",
      }}
      filterChips={isAdmin ? {
        options: CHIPS_ADMIN,
        value: chip,
        onChange: (next) => { setChip(next); setPage(0); },
        "aria-label": "Show clients by status",
      } : undefined}
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.clientId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/clients/${r.clientId}`)}
      />
      <NewClientDialog
        open={creating}
        onOpenChange={(o) => { if (!o) setCreating(false); }}
        onCreated={() => setCreating(false)}
      />
    </ListPage>
  );
}
